import React, { useState, useContext } from 'react';
import { Button, Checkbox, Space, DatePicker, Input, Upload, Typography, Alert } from 'antd';
import { ApplicationUIContext, Form, DrawerContent, MoneyInput } from '@yuhuinc/ui-library';
import { UploadOutlined } from '@ant-design/icons';
import { getTranslatedString } from 'helpers/i18n';

const { Title } = Typography;

const validationRules = [
  {
    required: true,
    message: getTranslatedString('required'),
    validateTrigger: 'onSubmit',
  },
];

const numberValidationRules = [
  {
    required: true,
    message: getTranslatedString('required'),
    validateTrigger: 'onSubmit',
    type: 'number',
  },
];

const dateValidationRules = [
  {
    required: true,
    message: getTranslatedString('required'),
    validateTrigger: 'onSubmit',
    transform(value) {
      if (value?.format) {
        return value.format('YYYY-MM-DD');
      }

      return value;
    },
  },
];

const atLeastOneValueIsSelectedValidator = (_, value) =>
  value?.length
    ? Promise.resolve()
    : Promise.reject(
        new Error(
          getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.contactMethod.selectAtLeastOneContactMethod'
          )
        )
      );

const getFileFromEvent = (event) => {
  if (Array.isArray(event)) {
    return event;
  }

  return event?.fileList;
};

const AddInsurance = ({ onSubmit, onCancel }) => {
  const [form] = Form.useForm();
  const { isMobile } = useContext(ApplicationUIContext);
  const [hasError, setHasError] = useState(false);

  const onError = ({ errorFields }) => {
    setHasError(errorFields.length > 1);
  };

  return (
    <DrawerContent>
      <Title level={isMobile ? 3 : 4} style={{ margin: 0 }}>
        {getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.insuranceDetails'
        )}
      </Title>
      {hasError && (
        <Alert message={getTranslatedString('errorsOnThisPage')} type="error" showIcon />
      )}
      <Form
        form={form}
        layout="vertical"
        size={isMobile && 'large'}
        gap={16}
        initialValues={{}}
        onFinish={onSubmit}
        onFinishFailed={onError}
      >
        <Form.Item
          name="policy_provider"
          rules={validationRules}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.policyProvider'
          )}
          hasFeedback
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="policy_number"
          rules={validationRules}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.policyNumber'
          )}
          hasFeedback
        >
          <Input />
        </Form.Item>
        <Form.Row>
          <Form.Item
            name="valid_from"
            rules={dateValidationRules}
            label={getTranslatedString(
              'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.validFrom'
            )}
            hasFeedback
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            name="valid_to"
            rules={dateValidationRules}
            label={getTranslatedString(
              'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.validTo'
            )}
            hasFeedback
          >
            <DatePicker />
          </Form.Item>
        </Form.Row>
        <Form.Item
          name="coverage"
          rules={numberValidationRules}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.coverage'
          )}
          extra={getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.coverage.caption'
          )}
          hasFeedback
        >
          <MoneyInput />
        </Form.Item>
        <Form.Item
          name="insured"
          rules={[{ validator: atLeastOneValueIsSelectedValidator }]}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.insured'
          )}
          hasFeedback
          required
        >
          <Checkbox.Group>
            <Space direction="vertical">
              <Checkbox value="1">Geraldine Blanchard</Checkbox>
              <Checkbox value="2">Tierra Singh</Checkbox>
            </Space>
          </Checkbox.Group>
        </Form.Item>
        <Form.Item
          name="document"
          rules={[
            {
              message: getTranslatedString('required'),
              validateTrigger: 'onSubmit',
              validator(_, value) {
                if (!value || value.length === 0) {
                  return Promise.reject(new Error(getTranslatedString('required')));
                }

                return Promise.resolve();
              },
            },
          ]}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.uploadDocument'
          )}
          hasFeedback
          required
          valuePropName="fileList"
          getValueFromEvent={getFileFromEvent}
        >
          <Upload beforeUpload={() => false}>
            <Button size={isMobile && 'large'} icon={<UploadOutlined />}>
              {getTranslatedString(
                'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.upload'
              )}
            </Button>
          </Upload>
        </Form.Item>
        <Space size="middle">
          <Button type="primary" size={isMobile && 'large'} htmlType="submit">
            {getTranslatedString(
              'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.save'
            )}
          </Button>
          <Button size={isMobile && 'large'} htmlType="button" onClick={onCancel}>
            {getTranslatedString(
              'landlordPortal.applicationManagement.applicationOverview.applicants.insurance.cancel'
            )}
          </Button>
        </Space>
      </Form>
    </DrawerContent>
  );
};

export default AddInsurance;
